<template>
  <div class="today-sales" :class="{ 'no-motion': reduceMotion }" :style="rootVars">
    <div class="money-board">
      <div class="label-left">
        <div class="label-line">{{ periodLabel }}</div>
        <div class="label-line">码洋</div>
      </div>
      <div class="digits">
        <template v-for="(item, index) in displayItems">
          <span v-if="item.type === 'comma'" :key="`comma-${index}`" class="comma">,</span>
          <div v-else :key="`digit-${index}`" class="digit-box">
            <div class="digit-box-wrap">
              <!-- 数字滚动容器 -->
              <div class="scroll-container">
                <div 
                  class="scroll-numbers"
                  :style="getScrollStyle(item)"
                >
                  <div 
                    v-for="(num, idx) in scrollNumbers" 
                    :key="`scroll-${index}-${idx}-${num}`" 
                    class="scroll-digit"
                  >
                    {{ num }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="label-right">元</div>
    </div>
    <!-- 底部会员统计信息行 -->
    <div class="bottom-info">
      <div class="info-item">
        会员总数：<span class="info-val" ref="memTotalRef">{{ memberTotal }}</span>
      </div>
      <div class="info-item">
        活跃会员数：<span class="info-val" ref="memActiveRef">{{ memberActive }}</span>
      </div>
      <div class="info-item">
        新增会员数：<span class="info-val" ref="memNewRef">{{ memberNew }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { CountUp } from 'countup.js';
import numberCardBg from '@/assets/images/number_card_bg.png'

// 常量定义
const CONSTANTS = {
  DISPLAY_LENGTH: 9,
  DEFAULT_SCROLL_DURATION: 600, // 滚动动画时长
  DEFAULT_DELAY_BETWEEN_DIGITS: 100, // 数字间延迟
  DEFAULT_EASING: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  MEMBER_TYPES: {
    TOTAL: '总会员数',
    ACTIVE: '活跃会员数',
    NEW: '新增会员数'
  },
  PERIOD_LABELS: {
    today: '今日',
    week: '本周',
    month: '本月'
  }
}

export default {
  name: 'TodaySales',
  props: {
    // 是否在挂载后播放入场动画
    animateOnMount: { 
      type: Boolean, 
      default: true 
    },
    // 滚动动画时长（毫秒）
    scrollDuration: { 
      type: Number, 
      default: CONSTANTS.DEFAULT_SCROLL_DURATION,
      validator: (value) => value > 0
    },
    // 数字间延迟（毫秒）
    delayBetweenDigits: { 
      type: Number, 
      default: CONSTANTS.DEFAULT_DELAY_BETWEEN_DIGITS,
      validator: (value) => value >= 0
    },
    // 缓动函数
    scrollEasing: { 
      type: String, 
      default: CONSTANTS.DEFAULT_EASING 
    }
  },
  data() {
    return {
      displayLength: CONSTANTS.DISPLAY_LENGTH,
      displayItems: [],
      scrollNumbers: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0], // 0-9循环，末尾多一个0用于平滑过渡
      animationFrameId: null,
      lastGroupedStr: '',
      queuedTargetStr: '',
      reduceMotion: false,
      reduceMotionMql: null,
      animationQueue: [],
      isAnimating: false,
      // CountUp实例存储
      countUpInstances: {
        memberTotal: null,
        memberActive: null,
        memberNew: null
      },
      // 上一次的数值，用于检测变化
      previousValues: {
        memberTotal: 0,
        memberActive: 0,
        memberNew: 0
      }
    }
  },
  computed: {
    ...mapState('todaySales', ['todaySales']),
    
    // 缓存期间标签
    periodLabel() {
      const period = this.todaySales?.period || 'today'
      return CONSTANTS.PERIOD_LABELS[period] || CONSTANTS.PERIOD_LABELS.today
    },
    
    // 原始销售额（数字）- 增加边界检查
    rawAmount() {
      const amount = this.todaySales?.totalAmount
      if (amount === null || amount === undefined) return 0
      const num = Math.round(Number(amount) || 0)
      return Math.max(num, 0)
    },
    
    // 补零后的纯数字字符串
    paddedStr() {
      const str = String(this.rawAmount)
      return str.length < this.displayLength 
        ? str.padStart(this.displayLength, '0') 
        : str
    },
    
    // 每三位添加逗号分隔
    groupedStr() {
      return this.paddedStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    // 会员统计映射 - 优化性能
    membersMap() {
      const members = this.todaySales?.members
      if (!Array.isArray(members)) return {}
      
      return members.reduce((map, member) => {
        if (member && typeof member === 'object') {
          const type = member.type || ''
          const value = Number(member.sl) || 0
          if (type) map[type] = value
        }
        return map
      }, {})
    },
    
    // 获取当前周期的会员数据
    currentPeriodMembers() {
      const period = this.todaySales?.period || 'today'
      return this.todaySales?.membersByPeriod?.[period] || { new: 0, active: 0, total: 0 }
    },
    
    memberTotal() {
      return this.currentPeriodMembers.total || this.membersMap[CONSTANTS.MEMBER_TYPES.TOTAL] || 0
    },
    
    memberActive() {
      return this.currentPeriodMembers.active || this.membersMap[CONSTANTS.MEMBER_TYPES.ACTIVE] || 0
    },
    
    memberNew() {
      return this.currentPeriodMembers.new || this.membersMap[CONSTANTS.MEMBER_TYPES.NEW] || 0
    },
    
    // 根级CSS变量，统一动画时长与缓动
    rootVars() {
      const duration = this.reduceMotion ? 0 : (this.scrollDuration || CONSTANTS.DEFAULT_SCROLL_DURATION)
      const easing = this.scrollEasing || CONSTANTS.DEFAULT_EASING
      return {
        '--scroll-duration': `${duration}ms`,
        '--scroll-easing': easing,
        '--digit-height': '75px' // 单个数字高度
      }
    }
  },
  watch: {
    groupedStr: {
      deep: false,
      immediate: true,
      handler(newStr, oldStr) {
        // 避免重复更新相同数据
        const prevStr = typeof oldStr === 'string' ? oldStr : this.lastGroupedStr
        if (newStr === prevStr) return
        
        // 如果显示项为空，直接初始化
        if (!this.displayItems.length) {
          this.initDisplay(newStr)
          this.lastGroupedStr = newStr
          return
        }
        
        // 执行数字滚动动画
        this.updateDisplay(newStr, prevStr)
        this.lastGroupedStr = newStr
      }
    },
    
    // 监听会员统计数值变化
    memberTotal: {
      immediate: true,
      handler(newVal, oldVal) {
        this.handleCountUpAnimation('memTotal', newVal, oldVal)
      }
    },
    
    memberActive: {
      immediate: true,
      handler(newVal, oldVal) {
        this.handleCountUpAnimation('memActive', newVal, oldVal)
      }
    },
    
    memberNew: {
      immediate: true,
      handler(newVal, oldVal) {
        this.handleCountUpAnimation('memNew', newVal, oldVal)
      }
    }
  },
  mounted() {
    // 监听系统无动画偏好
    this.setupReduceMotion()
    // 预加载数字卡背景，避免首帧闪烁
    this.preloadAssets()
  },
  
  beforeDestroy() {
    // 清理无动画监听
    if (this.reduceMotionMql && this.reduceMotionMql.removeEventListener) {
      this.reduceMotionMql.removeEventListener('change', this.onReduceMotionChange)
    }
    // 清理动画
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
    }
    this.animationQueue = []
    
    // 清理CountUp实例
    Object.values(this.countUpInstances).forEach(instance => {
      if (instance && typeof instance.reset === 'function') {
        instance.reset()
      }
    })
    this.countUpInstances = {}
  },
  
  methods: {
    // 优化数字格式化函数
    formatNumber(num) {
      const number = Math.round(Number(num) || 0)
      return String(number).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    // 获取滚动样式
    getScrollStyle(item) {
      if (!item || item.type !== 'digit') return {}
      
      const targetDigit = parseInt(item.targetValue || item.value || '0')
      const currentDigit = parseInt(item.currentValue || item.value || '0')
      
      // 计算滚动位置：每个数字占用75px高度
      const scrollPosition = -targetDigit * 75
      
      const style = {
        transform: `translateY(${scrollPosition}px)`,
        transition: item.animating && !this.reduceMotion 
          ? `transform var(--scroll-duration) var(--scroll-easing)` 
          : 'none'
      }
      
      return style
    },
    
    // 无动画偏好设置
    setupReduceMotion() {
      if (typeof window !== 'undefined' && window.matchMedia) {
        const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
        this.reduceMotionMql = mql
        this.reduceMotion = !!mql.matches
        this.onReduceMotionChange = (e) => {
          this.reduceMotion = !!e.matches
        }
        if (mql.addEventListener) {
          mql.addEventListener('change', this.onReduceMotionChange)
        }
      }
    },
    
    // 预加载资源，避免首次渲染闪烁
    preloadAssets() {
      try {
        const img = new Image()
        img.src = numberCardBg
      } catch (e) {
        // 静默失败
      }
    },
    
    // 初始化显示项
    initDisplay(str) {
      if (!str) {
        this.displayItems = []
        return
      }
      
      const items = []
      for (let i = 0; i < str.length; i++) {
        const char = str[i]
        if (char === ',') {
          items.push({ type: 'comma', char: ',' })
        } else {
          items.push({
            type: 'digit',
            value: char,
            currentValue: char,
            targetValue: char,
            animating: false
          })
        }
      }
      this.displayItems = items
    },
    
    // 更新显示
    updateDisplay(newStr, oldStr) {
      if (!newStr) return
      
      if (!this.displayItems.length) {
        this.initDisplay(newStr)
        this.lastGroupedStr = newStr
        return
      }
      
      this.ensureDisplaySync(newStr)
      
      // 收集需要动画的数字
      const animationItems = []
      
      for (let i = 0; i < newStr.length; i++) {
        const newChar = newStr[i]
        const currentItem = this.displayItems[i]
        
        if (!currentItem) continue
        
        if (newChar === ',') {
          if (currentItem.type !== 'comma') {
            this.$set(this.displayItems, i, { type: 'comma', char: ',' })
          }
          continue
        }
        
        if (currentItem.type !== 'digit') {
          this.$set(this.displayItems, i, {
            type: 'digit',
            value: '0',
            currentValue: '0',
            targetValue: newChar,
            animating: false
          })
          continue
        }
        
        if (newChar !== currentItem.value) {
          animationItems.push({ index: i, newChar })
        }
      }
      
      // 执行序列动画
      this.executeSequentialAnimation(animationItems)
    },
    
    // 确保显示同步
    ensureDisplaySync(str) {
      if (!Array.isArray(this.displayItems) || this.displayItems.length !== str.length) {
        this.initDisplay(str)
        return
      }
      
      // 校验并修复不一致的项
      for (let i = 0; i < str.length; i++) {
        const char = str[i]
        const currentItem = this.displayItems[i]
        
        if (char === ',' && (!currentItem || currentItem.type !== 'comma')) {
          this.$set(this.displayItems, i, { type: 'comma', char: ',' })
        } else if (char !== ',' && (!currentItem || currentItem.type !== 'digit')) {
          this.$set(this.displayItems, i, {
            type: 'digit',
            value: '0',
            currentValue: '0',
            targetValue: '0',
            animating: false
          })
        }
      }
    },
    
    // 执行序列动画
    async executeSequentialAnimation(animationItems) {
      if (!animationItems.length || this.isAnimating) return
      
      this.isAnimating = true
      
      // 如果启用了减少动画，直接更新所有值
      if (this.reduceMotion) {
        for (const { index, newChar } of animationItems) {
          const currentItem = this.displayItems[index]
          this.$set(this.displayItems, index, {
            ...currentItem,
            value: newChar,
            currentValue: newChar,
            targetValue: newChar,
            animating: false
          })
        }
        this.isAnimating = false
        return
      }
      
      // 按索引从大到小排序，实现从右到左的动画效果
      const digitItems = animationItems.filter(item => {
        const displayItem = this.displayItems[item.index]
        return displayItem && displayItem.type === 'digit'
      }).sort((a, b) => b.index - a.index)
      
      // 为每个数字分配延迟时间
      const animationPromises = digitItems.map(({ index, newChar }, delayIndex) => {
        const delay = delayIndex * this.delayBetweenDigits
        return this.animateDigitWithDelay(index, newChar, delay)
      })
      
      // 等待所有动画完成
      await Promise.all(animationPromises)
      this.isAnimating = false
    },
    
    // 带延迟的数字动画
    async animateDigitWithDelay(index, targetDigit, delay = 0) {
      // 如果有延迟，先等待
      if (delay > 0) {
        await this.delay(delay)
      }
      
      return this.animateDigit(index, targetDigit)
    },
    
    // 单个数字动画
    animateDigit(index, targetDigit) {
      return new Promise((resolve) => {
        const item = this.displayItems[index]
        if (!item || item.type !== 'digit') {
          resolve()
          return
        }
        
        // 更新目标值并开始动画
        this.$set(this.displayItems, index, {
          ...item,
          targetValue: String(targetDigit),
          animating: true
        })
        
        // 动画完成后的处理
        const duration = this.scrollDuration || CONSTANTS.DEFAULT_SCROLL_DURATION
        setTimeout(() => {
          this.$set(this.displayItems, index, {
            ...this.displayItems[index],
            value: String(targetDigit),
            currentValue: String(targetDigit),
            animating: false
          })
          resolve()
        }, duration)
      })
    },
    
    // 延时函数
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    
    // 处理CountUp动画
    handleCountUpAnimation(type, newVal, oldVal) {
      // 确保在DOM更新后执行
      this.$nextTick(() => {
        const refName = `${type}Ref`
        const targetElement = this.$refs[refName]
        
        if (!targetElement) {
          console.warn(`CountUp target element not found: ${refName}`)
          return
        }
        
        // 数值转换和验证
        const currentValue = Number(newVal) || 0
        const previousValue = this.previousValues[type] || 0
        
        // 如果数值没有变化，跳过动画
        if (currentValue === previousValue && this.countUpInstances[type]) {
          return
        }
        
        // 更新记录的数值
        this.previousValues[type] = currentValue
        
        // 如果已存在CountUp实例，使用update方法更新数值
        if (this.countUpInstances[type]) {
          try {
            this.countUpInstances[type].update(currentValue)
          } catch (error) {
            console.error(`CountUp update error for ${type}:`, error)
            // 如果update失败，直接显示数值
            targetElement.textContent = this.formatNumber(currentValue)
          }
          return
        }
        
        // 首次创建CountUp实例
        const options = {
          startVal: 0, // 从0开始
          duration: this.reduceMotion ? 0 : 2, // 动画时长2秒
          useEasing: true,
          useGrouping: true, // 使用千分位分隔符
          separator: ',',
          decimal: '.',
          decimalPlaces: 0,
          prefix: '',
          suffix: ''
        }
        
        try {
          // 创建新的CountUp实例
          const countUp = new CountUp(targetElement, currentValue, options)
          
          if (!countUp.error) {
            this.countUpInstances[type] = countUp
            // 开始动画
            countUp.start()
          } else {
            console.error(`CountUp error for ${type}:`, countUp.error)
            // 如果CountUp失败，直接显示数值
            targetElement.textContent = this.formatNumber(currentValue)
          }
        } catch (error) {
          console.error(`CountUp initialization error for ${type}:`, error)
          // 如果CountUp失败，直接显示数值
          targetElement.textContent = this.formatNumber(currentValue)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.today-sales {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 160px;
  contain: layout paint style;
  content-visibility: auto;

  .money-board {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 22px;
    flex-wrap: nowrap;
    position: relative;
  }

  .label-left {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.4;
    color: #34E9FD;
    margin-top: 20px;
    position: absolute;
    left: 24px;
    
    .label-line { 
      font-size: 17px; 
      font-weight: bold;
    }
  }

  .digits {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    white-space: nowrap;
    user-select: none;
    overflow: hidden;
  }

  .digit-box {
    width: 95px;
    height: 125px;
    margin: 0 -16.5px;
    position: relative;
    will-change: transform;
    content-visibility: auto;
    contain: layout paint;
    contain-intrinsic-size: 95px 125px;
    background: url("@/assets/images/number_card_bg.png") no-repeat center center;
    background-size: 100% auto;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .comma {
    height: 125px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 56px;
    font-weight: 700;
    color: #FFF;
    margin: 0 8px;
    margin-right: 14px;
  }

  .digit-box-wrap {
    width: 50px;
    height: 75px;
    position: relative;
    overflow: hidden;
  }

  // 数字滚动容器
  .scroll-container {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .scroll-numbers {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    will-change: transform;
    transform: translateZ(0);
  }

  .scroll-digit {
    width: 100%;
    height: var(--digit-height);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-weight: 800;
    font-size: 72px;
    line-height: 1.05;
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
    text-align: center;
    pointer-events: none;
    flex-shrink: 0;
  }

  // 系统无动画偏好降级
  @media (prefers-reduced-motion: reduce) {
    .scroll-numbers {
      transition: none !important;
    }
  }

  .today-sales.no-motion {
    .scroll-numbers {
      transition: none !important;
    }
  }

  .label-right {
    font-size: 20px;
    color: #FFFFFF;
    font-weight: bold;
    margin-top: 48px;
    position: absolute;
    right: 42px;
  }

  .bottom-info {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0 36px;
    margin-top: -5px;
    font-family: 'Microsoft YaHei', Arial, sans-serif;
    color: #FFFFFF;
    line-height: 1;
    margin-bottom: 12px;
  }

  .info-item {
    white-space: nowrap;
    font-size: 12px;
    font-weight: 400;
    transition: color 0.3s ease;
    color: #fff;
    cursor: default;
    
    &:hover {
      color: #34E9FD;
    }
  }

  .info-val {
    font-size: 12px;
    font-weight: 400;
  }
}

// 加载状态样式
.today-sales.loading {
  .digit-box {
    opacity: 0.6;
    animation: pulse 1.5s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

// 错误状态样式
.today-sales.error {
  .digit-box {
    background-color: rgba(255, 0, 0, 0.1);
    border: 1px solid rgba(255, 0, 0, 0.3);
  }
}

// 性能优化：减少重绘
.scroll-numbers {
  will-change: transform;
  backface-visibility: hidden;
}

.digit-box:not(.animating) .scroll-numbers {
  will-change: auto;
}
</style>